import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useState } from 'react'
import { storageAPI } from '../api/client'
import {
  HardDrive, Database, Cloud, FolderOpen,
  TrendingUp, TrendingDown, AlertTriangle, RefreshCw,
  Clock, Calendar
} from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts'

// Format bytes to human readable
function formatBytes(bytes, decimals = 2) {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i]
}

// Format change with sign
function formatChange(bytes) {
  if (bytes === null || bytes === undefined) return '-'
  const sign = bytes >= 0 ? '+' : ''
  return sign + formatBytes(Math.abs(bytes))
}

// Progress bar component
function ProgressBar({ percent, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
  }

  const barColor = percent >= 90 ? 'red' : percent >= 80 ? 'yellow' : color

  return (
    <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
      <div
        className={`h-full ${colorClasses[barColor]} transition-all duration-500`}
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
  )
}

// Stat card component
function StatCard({ icon: Icon, title, value, subtitle, color = 'blue' }) {
  return (
    <div className="glass p-5 rounded-xl">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg bg-${color}-500/20`}>
          <Icon className={`w-5 h-5 text-${color}-400`} />
        </div>
        <span className="text-white/60 text-sm">{title}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {subtitle && <div className="text-sm text-white/40 mt-1">{subtitle}</div>}
    </div>
  )
}

// Alert banner component
function AlertBanner({ alerts }) {
  if (!alerts || alerts.length === 0) return null

  return (
    <div className="space-y-2 mb-6">
      {alerts.map((alert, idx) => (
        <div
          key={idx}
          className={`p-4 rounded-lg flex items-center gap-3 ${
            alert.level === 'critical'
              ? 'bg-red-500/20 border border-red-500/50'
              : 'bg-yellow-500/20 border border-yellow-500/50'
          }`}
        >
          <AlertTriangle className={`w-5 h-5 ${
            alert.level === 'critical' ? 'text-red-400' : 'text-yellow-400'
          }`} />
          <span className="text-white">{alert.message}</span>
          <span className="ml-auto text-white/60">{alert.used_percent.toFixed(1)}%</span>
        </div>
      ))}
    </div>
  )
}

// Storage chart component
function StorageChart({ data, title }) {
  if (!data || data.length === 0) {
    return (
      <div className="glass p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
        <div className="h-64 flex items-center justify-center text-white/40">
          Данных пока нет. Статистика собирается каждые 6 часов.
        </div>
      </div>
    )
  }

  const chartData = data.map(point => ({
    time: new Date(point.time).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }),
    gb: (point.bytes / 1024 / 1024 / 1024).toFixed(2)
  }))

  return (
    <div className="glass p-6 rounded-xl">
      <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
          <XAxis
            dataKey="time"
            stroke="#a0aec0"
            fontSize={12}
            tickMargin={10}
          />
          <YAxis
            stroke="#a0aec0"
            fontSize={12}
            tickFormatter={(v) => `${v} GB`}
          />
          <Tooltip
            contentStyle={{
              background: 'rgba(0,0,0,0.9)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px'
            }}
            labelStyle={{ color: '#a0aec0' }}
            formatter={(value) => [`${value} GB`, 'Использование']}
          />
          <Line
            type="monotone"
            dataKey="gb"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ fill: '#3b82f6', strokeWidth: 0, r: 3 }}
            activeDot={{ r: 5, fill: '#60a5fa' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

export default function StorageMonitor() {
  const [period, setPeriod] = useState('7d')
  const queryClient = useQueryClient()

  // Fetch current storage stats
  const { data: current, isLoading: loadingCurrent } = useQuery(
    'storageCurrent',
    storageAPI.getCurrent,
    { refetchInterval: 30000 } // Refresh every 30 seconds
  )

  // Fetch history
  const { data: history, isLoading: loadingHistory } = useQuery(
    ['storageHistory', period],
    () => storageAPI.getHistory(period),
    { refetchInterval: 60000 } // Refresh every minute
  )

  // Fetch alerts
  const { data: alerts } = useQuery('storageAlerts', storageAPI.getAlerts, {
    refetchInterval: 30000
  })

  // Manual collection trigger
  const collectMutation = useMutation(storageAPI.triggerCollection, {
    onSuccess: () => {
      queryClient.invalidateQueries('storageCurrent')
      queryClient.invalidateQueries('storageHistory')
    }
  })

  if (loadingCurrent) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    )
  }

  const s3 = current?.s3
  const disk = current?.disk
  const directories = current?.directories
  const changes = history?.changes || {}
  const forecast = history?.forecast || {}

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <HardDrive className="w-7 h-7 text-blue-400" />
            Монитор хранилища
          </h1>
          <p className="text-white/60 mt-1">
            Последнее обновление: {current?.collected_at
              ? new Date(current.collected_at).toLocaleString('ru-RU')
              : 'Никогда'}
          </p>
        </div>
        <button
          onClick={() => collectMutation.mutate()}
          disabled={collectMutation.isLoading}
          className="btn-primary flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${collectMutation.isLoading ? 'animate-spin' : ''}`} />
          Обновить сейчас
        </button>
      </div>

      {/* Alerts */}
      <AlertBanner alerts={alerts} />

      {/* Current Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* S3 Storage */}
        <div className="glass p-5 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-cyan-500/20">
              <Cloud className="w-5 h-5 text-cyan-400" />
            </div>
            <span className="text-white/60 text-sm">Хранилище S3</span>
          </div>
          {s3 ? (
            <>
              <div className="text-2xl font-bold text-white">
                {formatBytes(s3.used_bytes)} / {formatBytes(s3.limit_bytes)}
              </div>
              <ProgressBar percent={s3.used_percent} color="cyan" />
              <div className="mt-2 text-sm text-white/40">
                Использовано: {s3.used_percent.toFixed(1)}%
              </div>
              {s3.buckets && (
                <div className="mt-3 text-xs text-white/40 space-y-1">
                  {Object.entries(s3.buckets).map(([name, bytes]) => (
                    <div key={name} className="flex justify-between">
                      <span>{name}:</span>
                      <span>{formatBytes(bytes)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-white/40">Не настроено</div>
          )}
        </div>

        {/* Disk Storage */}
        <div className="glass p-5 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Database className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-white/60 text-sm">Дисковое пространство</span>
          </div>
          {disk ? (
            <>
              <div className="text-2xl font-bold text-white">
                {formatBytes(disk.used_bytes)} / {formatBytes(disk.total_bytes)}
              </div>
              <ProgressBar percent={disk.used_percent} color="blue" />
              <div className="mt-2 text-sm text-white/40">
                Использовано: {disk.used_percent.toFixed(1)}%, свободно {formatBytes(disk.free_bytes)}
              </div>
            </>
          ) : (
            <div className="text-white/40">Недоступно</div>
          )}
        </div>

        {/* Directories */}
        <div className="glass p-5 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <FolderOpen className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-white/60 text-sm">Директории</span>
          </div>
          {directories ? (
            <div className="space-y-2">
              {directories.telesun_all && (
                <div className="flex justify-between text-white">
                  <span className="text-white/60">Проект telesun_all:</span>
                  <span>{formatBytes(directories.telesun_all)}</span>
                </div>
              )}
              {directories.aggregator_images && (
                <div className="flex justify-between text-white">
                  <span className="text-white/60">Папка images:</span>
                  <span>{formatBytes(directories.aggregator_images)}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-white/40">Недоступно</div>
          )}
        </div>

        {/* Forecast */}
        <div className="glass p-5 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-green-500/20">
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <span className="text-white/60 text-sm">Прогноз</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-white">
              <span className="text-white/60">S3 заполнится через:</span>
              <span className={forecast.s3_days_until_full
                ? forecast.s3_days_until_full < 30 ? 'text-red-400' : 'text-green-400'
                : 'text-white/40'
              }>
                {forecast.s3_days_until_full ? `~${forecast.s3_days_until_full} дн.` : '-'}
              </span>
            </div>
            <div className="flex justify-between text-white">
              <span className="text-white/60">Диск заполнится через:</span>
              <span className={forecast.disk_days_until_full
                ? forecast.disk_days_until_full < 30 ? 'text-red-400' : 'text-green-400'
                : 'text-white/40'
              }>
                {forecast.disk_days_until_full ? `~${forecast.disk_days_until_full} дн.` : '-'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Growth Stats */}
      <div className="glass p-5 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-white/60" />
          Анализ роста
        </h3>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-4 bg-white/5 rounded-lg">
            <div className="text-white/60 text-sm mb-1">Последние 6 часов</div>
            <div className="text-lg font-semibold text-white">
              {changes['6h']?.disk !== null ? formatChange(changes['6h']?.disk) : '-'}
            </div>
            <div className="text-xs text-white/40">Диск</div>
          </div>
          <div className="text-center p-4 bg-white/5 rounded-lg">
            <div className="text-white/60 text-sm mb-1">Последние 24 часа</div>
            <div className="text-lg font-semibold text-white">
              {changes['24h']?.disk !== null ? formatChange(changes['24h']?.disk) : '-'}
            </div>
            <div className="text-xs text-white/40">Диск</div>
          </div>
          <div className="text-center p-4 bg-white/5 rounded-lg">
            <div className="text-white/60 text-sm mb-1">Последние 7 дней</div>
            <div className="text-lg font-semibold text-white">
              {changes['7d']?.disk !== null ? formatChange(changes['7d']?.disk) : '-'}
            </div>
            <div className="text-xs text-white/40">Диск</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-cyan-500/10 rounded-lg">
            <div className="text-cyan-400/60 text-sm mb-1">Последние 6 часов</div>
            <div className="text-lg font-semibold text-cyan-400">
              {changes['6h']?.s3 !== null ? formatChange(changes['6h']?.s3) : '-'}
            </div>
            <div className="text-xs text-cyan-400/40">S3</div>
          </div>
          <div className="text-center p-4 bg-cyan-500/10 rounded-lg">
            <div className="text-cyan-400/60 text-sm mb-1">Последние 24 часа</div>
            <div className="text-lg font-semibold text-cyan-400">
              {changes['24h']?.s3 !== null ? formatChange(changes['24h']?.s3) : '-'}
            </div>
            <div className="text-xs text-cyan-400/40">S3</div>
          </div>
          <div className="text-center p-4 bg-cyan-500/10 rounded-lg">
            <div className="text-cyan-400/60 text-sm mb-1">Последние 7 дней</div>
            <div className="text-lg font-semibold text-cyan-400">
              {changes['7d']?.s3 !== null ? formatChange(changes['7d']?.s3) : '-'}
            </div>
            <div className="text-xs text-cyan-400/40">S3</div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Calendar className="w-5 h-5 text-white/60" />
          История использования
        </h3>
        <div className="flex gap-2">
          {['1d', '7d', '30d'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                period === p
                  ? 'bg-blue-500 text-white'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StorageChart
          data={history?.s3_total}
          title="Использование S3"
        />
        <StorageChart
          data={history?.disk_used}
          title="Использование диска"
        />
      </div>
    </div>
  )
}
